import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@clareefai/ui';
import {
  ArrowLeft,
  Loader2,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  Users,
  Eye,
  MousePointerClick,
  MessageSquare,
  Clock,
  TrendingUp,
} from 'lucide-react';
import type {
  TestimonialResponse,
  DealRoomStatus,
  CreateDealRoomRequest,
  UpdateDealRoomRequest,
} from '@clareefai/types';
import {
  useGetDealRoomQuery,
  useCreateDealRoomMutation,
  useUpdateDealRoomMutation,
  useGetAllTestimonialsQuery,
} from '../../store/vendorApi';
import SelectReviewsModal from '../../components/widget/SelectReviewsModal';

const DEFAULT_HEADING = 'Trusted by teams like yours';
const DEFAULT_ACCENT = '#4F46E5';

const formatDuration = (ms: number): string => {
  if (!ms || ms < 1000) return '0s';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const DealRoomEditor: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreate = !id || id === 'new';

  const { data: detail, isLoading } = useGetDealRoomQuery(id as string, { skip: isCreate });
  const { data: testimonials } = useGetAllTestimonialsQuery({});
  const [createDealRoom, { isLoading: isCreating }] = useCreateDealRoomMutation();
  const [updateDealRoom, { isLoading: isUpdating }] = useUpdateDealRoomMutation();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Override pattern (mirrors ReviewsWidgetSettings): local edits override the
  // loaded detail without a sync effect. `undefined` = untouched.
  const [name, setName] = useState<string | null>(null);
  const [prospectName, setProspectName] = useState<string | null>(null);
  const [prospectCompany, setProspectCompany] = useState<string | null>(null);
  const [selectedReviewIds, setSelectedReviewIds] = useState<string[] | null>(null);
  const [heading, setHeading] = useState<string | null>(null);
  const [intro, setIntro] = useState<string | null>(null);
  const [accentColor, setAccentColor] = useState<string | null>(null);
  const [showPromoters, setShowPromoters] = useState<boolean | null>(null);
  const [ctaHeading, setCtaHeading] = useState<string | null>(null);
  const [ctaLabel, setCtaLabel] = useState<string | null>(null);
  // undefined = untouched; '' = cleared.
  const [expiryDate, setExpiryDate] = useState<string | undefined>(undefined);

  // All published/featured testimonials, every type (REVIEW / VIDEO / USE_CASE).
  const eligible: TestimonialResponse[] = [
    ...(testimonials?.published ?? []),
    ...(testimonials?.featured ?? []),
  ];

  if (!isCreate && isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  // Effective values
  const eName = name ?? detail?.name ?? '';
  const eProspectName = prospectName ?? detail?.prospectName ?? '';
  const eProspectCompany = prospectCompany ?? detail?.prospectCompany ?? '';
  const eSelectedIds = selectedReviewIds ?? detail?.selectedReviewIds ?? [];
  const eHeading = heading ?? detail?.heading ?? DEFAULT_HEADING;
  const eIntro = intro ?? detail?.intro ?? '';
  const eAccent = accentColor ?? detail?.accentColor ?? DEFAULT_ACCENT;
  const eShowPromoters = showPromoters ?? detail?.showPromoters ?? true;
  const eCtaHeading = ctaHeading ?? detail?.ctaHeading ?? '';
  const eCtaLabel = ctaLabel ?? detail?.ctaLabel ?? '';
  const eExpiry =
    expiryDate === undefined ? (detail?.expiresAt ? detail.expiresAt.slice(0, 10) : '') : expiryDate;

  const selectedReviews = eSelectedIds
    .map((rid) => eligible.find((r) => r.id === rid))
    .filter((r): r is TestimonialResponse => Boolean(r));

  const reviewName = (rid: string) => eligible.find((r) => r.id === rid)?.promoterName ?? rid;

  const fieldClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent';

  const validate = (): string | null => {
    if (!eName.trim()) return t('dealRooms.nameRequired', 'Give your deal room a name.');
    if (eSelectedIds.length === 0) return t('dealRooms.reviewsRequired', 'Select at least one review.');
    return null;
  };

  const buildExpiryIso = (): string | null => {
    if (!eExpiry) return null;
    // End of the chosen day, UTC.
    return new Date(`${eExpiry}T23:59:59.000Z`).toISOString();
  };

  const handleCreate = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const body: CreateDealRoomRequest = {
      name: eName.trim(),
      prospectName: eProspectName.trim() || null,
      prospectCompany: eProspectCompany.trim() || null,
      selectedReviewIds: eSelectedIds,
      heading: eHeading.trim() || null,
      intro: eIntro.trim() || null,
      accentColor: eAccent,
      showPromoters: eShowPromoters,
      ctaHeading: eCtaHeading.trim() || null,
      ctaLabel: eCtaLabel.trim() || null,
      expiresAt: buildExpiryIso(),
    };
    try {
      const created = await createDealRoom(body).unwrap();
      toast.success(t('dealRooms.created', 'Deal room created'));
      navigate(`/deal-rooms/${created.id}`);
    } catch {
      toast.error(t('dealRooms.saveError', 'Could not save. Please try again.'));
    }
  };

  const handleUpdate = async (overrides: UpdateDealRoomRequest = {}) => {
    if (!detail) return;
    const err = validate();
    if (err && Object.keys(overrides).length === 0) {
      toast.error(err);
      return;
    }
    const hadExpiry = Boolean(detail.expiresAt);
    const data: UpdateDealRoomRequest = {
      name: eName.trim(),
      prospectName: eProspectName.trim() || null,
      prospectCompany: eProspectCompany.trim() || null,
      selectedReviewIds: eSelectedIds,
      heading: eHeading.trim() || null,
      intro: eIntro.trim() || null,
      accentColor: eAccent,
      showPromoters: eShowPromoters,
      ctaHeading: eCtaHeading.trim() || null,
      ctaLabel: eCtaLabel.trim() || null,
      expiresAt: buildExpiryIso() ?? undefined,
      clearExpiry: hadExpiry && !eExpiry,
      ...overrides,
    };
    try {
      await updateDealRoom({ id: detail.id, data }).unwrap();
      toast.success(t('dealRooms.saved', 'Deal room saved'));
    } catch {
      toast.error(t('dealRooms.saveError', 'Could not save. Please try again.'));
    }
  };

  const copyLink = async () => {
    if (!detail) return;
    await navigator.clipboard.writeText(detail.publicUrl);
    setCopied(true);
    toast.success(t('dealRooms.linkCopied', 'Share link copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleStatus = () => {
    if (!detail) return;
    const next: DealRoomStatus = detail.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED';
    handleUpdate({ status: next });
  };

  const saving = isCreating || isUpdating;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/deal-rooms')}
          className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">
            {isCreate ? t('dealRooms.createTitle', 'New deal room') : eName || t('dealRooms.title', 'Deal Room')}
          </h1>
          {!isCreate && detail && (
            <p className="text-sm text-gray-500">
              {t('dealRooms.lastUpdated', 'Updated')} {new Date(detail.updatedAt).toLocaleString()}
            </p>
          )}
        </div>
        <button
          onClick={() => (isCreate ? handleCreate() : handleUpdate())}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {isCreate ? t('dealRooms.create', 'Create deal room') : t('dealRooms.save', 'Save')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Configuration column */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
          <h2 className="font-semibold text-gray-900">{t('dealRooms.configuration', 'Configuration')}</h2>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dealRooms.name', 'Name (internal)')}</label>
            <input
              type="text"
              value={eName}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('dealRooms.namePlaceholder', 'e.g. Acme Corp — Q3 evaluation') as string}
              className={fieldClass}
            />
          </div>

          {/* Prospect */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('dealRooms.prospectName', 'Prospect name')}</label>
              <input type="text" value={eProspectName} onChange={(e) => setProspectName(e.target.value)} className={fieldClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('dealRooms.prospectCompany', 'Prospect company')}</label>
              <input type="text" value={eProspectCompany} onChange={(e) => setProspectCompany(e.target.value)} className={fieldClass} />
            </div>
          </div>

          {/* Reviews */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dealRooms.reviews', 'Reviews')}</label>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200"
            >
              {t('dealRooms.selectReviews', 'Select reviews ({{count}})', { count: eSelectedIds.length })}
            </button>
            {selectedReviews.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedReviews.map((r) => (
                  <span key={r.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700">
                    {r.type === 'VIDEO' ? '🎥' : r.type === 'USE_CASE' ? '📄' : '⭐'} {r.promoterName}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Heading + accent */}
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('dealRooms.heading', 'Heading')}</label>
              <input type="text" value={eHeading} onChange={(e) => setHeading(e.target.value)} className={fieldClass} />
            </div>
            <input
              type="color"
              value={eAccent}
              onChange={(e) => setAccentColor(e.target.value)}
              className="h-10 w-14 rounded border border-gray-300 flex-shrink-0"
              title={t('dealRooms.accentColor', 'Accent color') as string}
            />
          </div>

          {/* Intro */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dealRooms.intro', 'Intro message')}</label>
            <textarea
              value={eIntro}
              onChange={(e) => setIntro(e.target.value)}
              rows={2}
              placeholder={t('dealRooms.introPlaceholder', 'A short note shown above the reviews.') as string}
              className={`${fieldClass} resize-none`}
            />
          </div>

          {/* Show promoters */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">{t('dealRooms.showPromoters', 'Show promoter profiles')}</p>
              <p className="text-xs text-gray-500">{t('dealRooms.showPromotersHint', 'Displays the people behind the selected reviews.')}</p>
            </div>
            <button
              onClick={() => setShowPromoters(!eShowPromoters)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eShowPromoters ? 'bg-purple-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eShowPromoters ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* CTA — always a "request a call" form; these customize its copy. */}
          <div className="space-y-3 border-t border-gray-100 pt-4">
            <label className="block text-sm font-medium text-gray-700">{t('dealRooms.cta', 'Request-a-call form')}</label>
            <input
              type="text"
              value={eCtaHeading}
              onChange={(e) => setCtaHeading(e.target.value)}
              placeholder={t('dealRooms.ctaHeadingPlaceholder', 'Interested? Let’s talk.') as string}
              className={fieldClass}
            />
            <input
              type="text"
              value={eCtaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
              placeholder={t('dealRooms.ctaLabelFormPlaceholder', 'Request a call') as string}
              className={fieldClass}
            />
            <p className="text-xs text-gray-500">
              {t('dealRooms.ctaFormHint', 'Visitors fill out a short form — submissions appear in your Demo Requests.')}
            </p>
          </div>

          {/* Expiry */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dealRooms.expiry', 'Expiry date (optional)')}</label>
            <input
              type="date"
              value={eExpiry}
              onChange={(e) => setExpiryDate(e.target.value)}
              className={fieldClass}
            />
            <p className="text-xs text-gray-500 mt-1">{t('dealRooms.expiryHint', 'After this date the link stops working. Leave empty for no expiry.')}</p>
          </div>
        </div>

        {/* Share + analytics column (edit mode only) */}
        <div className="space-y-6">
          {!isCreate && detail ? (
            <>
              {/* Share link */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-gray-900">{t('dealRooms.shareLink', 'Share link')}</h2>
                  <button
                    onClick={toggleStatus}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                      detail.status === 'DISABLED'
                        ? 'bg-red-50 border-red-200 text-red-700'
                        : 'bg-green-50 border-green-200 text-green-700'
                    }`}
                  >
                    {detail.status === 'DISABLED'
                      ? t('dealRooms.enable', 'Disabled — click to enable')
                      : t('dealRooms.disable', 'Active — click to disable')}
                  </button>
                </div>
                <div className="flex gap-2">
                  <input readOnly value={detail.publicUrl} onFocus={(e) => e.currentTarget.select()} className={`${fieldClass} font-mono text-xs bg-gray-50`} />
                  <button onClick={copyLink} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex-shrink-0">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? t('dealRooms.copied', 'Copied') : t('dealRooms.copy', 'Copy')}
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <a href={detail.publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-800">
                    <ExternalLink className="w-4 h-4" />
                    {t('dealRooms.openPage', 'Open page')}
                  </a>
                  <button
                    onClick={() => handleUpdate({ regenerateKey: true })}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-60"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {t('dealRooms.regenerate', 'Regenerate link')}
                  </button>
                </div>
                {detail.isExpired && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    {t('dealRooms.expiredWarning', 'This deal room has expired and is no longer viewable. Update the expiry date to reactivate it.')}
                  </p>
                )}
              </div>

              {/* Analytics */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                <h2 className="font-semibold text-gray-900">{t('dealRooms.analytics', 'Analytics')}</h2>
                <div className="grid grid-cols-2 gap-4">
                  <Stat icon={<Users className="w-4 h-4" />} color="blue" value={detail.analytics.uniqueVisitors.toLocaleString()} label={t('dealRooms.uniqueVisitors', 'Unique visitors')} />
                  <Stat icon={<Eye className="w-4 h-4" />} color="indigo" value={detail.analytics.totalVisits.toLocaleString()} label={t('dealRooms.totalVisits', 'Total visits')} />
                  <Stat icon={<Clock className="w-4 h-4" />} color="gray" value={formatDuration(detail.analytics.avgDurationMs)} label={t('dealRooms.avgTime', 'Avg. time on page')} />
                  <Stat icon={<MousePointerClick className="w-4 h-4" />} color="orange" value={detail.analytics.ctaClicks.toLocaleString()} label={t('dealRooms.ctaClicks', 'CTA clicks')} />
                  <Stat icon={<MessageSquare className="w-4 h-4" />} color="green" value={detail.analytics.leads.toLocaleString()} label={t('dealRooms.leads', 'Leads')} />
                  <Stat icon={<TrendingUp className="w-4 h-4" />} color="purple" value={`${Math.round(detail.analytics.ctaConversionRate * 1000) / 10}%`} label={t('dealRooms.conversion', 'Conversion rate')} />
                </div>

                {detail.analytics.perReview.length > 0 && (
                  <div className="pt-2">
                    <p className="text-sm font-medium text-gray-700 mb-2">{t('dealRooms.perReview', 'Per-review engagement')}</p>
                    <div className="space-y-1">
                      {detail.analytics.perReview.map((pr) => (
                        <div key={pr.reviewId} className="flex items-center justify-between text-sm border-b border-gray-50 py-1">
                          <span className="truncate text-gray-700">{reviewName(pr.reviewId)}</span>
                          <span className="flex items-center gap-3 text-xs text-gray-500 flex-shrink-0">
                            <span title={t('dealRooms.views', 'Views') as string}>👁 {pr.views}</span>
                            <span title={t('dealRooms.videoPlays', 'Video plays') as string}>▶ {pr.videoPlays}</span>
                            <span title={t('dealRooms.expands', 'Expands') as string}>⤢ {pr.expands}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
              {t('dealRooms.createHint', 'Save the deal room to get its shareable link and analytics.')}
            </div>
          )}
        </div>
      </div>

      <SelectReviewsModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        reviews={eligible}
        selectedIds={eSelectedIds}
        onChange={(ids) => setSelectedReviewIds(ids)}
      />
    </div>
  );
};

const STAT_COLORS: Record<string, string> = {
  blue: 'bg-blue-50 text-blue-700',
  indigo: 'bg-indigo-50 text-indigo-700',
  gray: 'bg-gray-50 text-gray-700',
  orange: 'bg-orange-50 text-orange-700',
  green: 'bg-green-50 text-green-700',
  purple: 'bg-purple-50 text-purple-700',
};

const Stat: React.FC<{ icon: React.ReactNode; color: string; value: string; label: string }> = ({ icon, color, value, label }) => (
  <div className={`rounded-lg p-4 ${STAT_COLORS[color] ?? STAT_COLORS.gray}`}>
    <div className="flex items-center gap-1.5 opacity-80">{icon}</div>
    <p className="text-2xl font-bold mt-1">{value}</p>
    <p className="text-xs text-gray-600 mt-0.5">{label}</p>
  </div>
);

export default DealRoomEditor;
